'use client'
import { type ComponentProps } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Pillow } from '@/components/Pillow/Pillow'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { useHasOffersFromMultipleTiers } from '../../PurchaseForm/sseHasOffersFromMultipleTiers'
import { useSelectedOffer } from '../../PurchaseForm/useSelectedOffer'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { DiscountHighlight } from '../DiscountHighlight'
import { SelectedOfferAnimatedPrice } from '../SelectedOfferAnimatedPrice/SelectedOfferAnimatedPrice'
import { productHeroPillow, subTypeBadge } from './ProductHeroPillow.css'

type ProductHeroPillowProps = ComponentProps<'div'> & {
  isCondensed?: boolean
}

export function ProductHeroPillow({ className, isCondensed, ...props }: ProductHeroPillowProps) {
  const { t } = useTranslation('common')
  const { displayNameFull, displayNameShort, pillowImage, purchaseFlowTagline } = useProductData()
  const { step } = usePriceCalculatorStep()
  const [selectedOffer] = useSelectedOffer()

  const hasTiers = useHasOffersFromMultipleTiers()

  const subType = hasTiers ? selectedOffer?.variant.displayNameSubtype : undefined

  const isViewingOffers = step === 'viewOffers'
  const productDisplayName = isViewingOffers ? displayNameShort : displayNameFull

  const isShowingPrice = isViewingOffers && selectedOffer != null

  return (
    <div className={clsx(productHeroPillow.base, isCondensed && productHeroPillow.condensed, className)} {...props}>
      <div className={sprinkles({ position: 'relative' })}>
        <Pillow size={{ _: 'xxlarge', lg: 'xxxlarge' }} {...pillowImage} imagePriority={true} />
        {subType && (
          <Badge className={subTypeBadge} size="responsive">
            {subType}
          </Badge>
        )}
      </div>

      <div className={yStack({ gap: 'md', alignItems: 'center', position: 'relative', textAlign: 'center' })}>
        <div>
          {step !== 'viewOffers' && (
            <Heading as="h1" variant="standard.18">
              <span>{productDisplayName}</span>
            </Heading>
          )}

          {!isShowingPrice && (
            <Text color="textSecondary" size="md">
              {purchaseFlowTagline}
            </Text>
          )}

          {isShowingPrice && (
            <div className={yStack({ alignItems: 'center' })}>
              <Text color="textSecondary" size="md">
                {t('YOUR_PRICE')}
              </Text>

              <SelectedOfferAnimatedPrice className={yStack({ alignItems: 'center' })} />
            </div>
          )}
        </div>

        {isShowingPrice && <DiscountHighlight selectedOffer={selectedOffer} />}
      </div>
    </div>
  )
}
